// Controller navigation for the DOM menus (ride-out lobby, pause/options). Polls the standard
// Gamepad mapping on its own rAF loop so it works before a game exists, moves a visible focus
// ring between the scope's controls by screen position, and tracks the active input device so
// the page can swap key names for PS5 glyphs (body.pad).
//
// Standard mapping: 0 ✕, 1 ○, 2 □, 3 △, 4 L1, 5 R1, 6 L2, 7 R2, 8 Create, 9 Options, 10 L3,
// 11 R3, 12-15 d-pad up/down/left/right, 16 PS, 17 touchpad. PS and touchpad are ignored here.

export type Device = 'kbm' | 'pad';

export interface NavScope {
  root: HTMLElement;
  /** controls that can take focus, in DOM order */
  selector: string;
  /** element to focus when the scope opens (or when focus is lost) */
  initial?: () => HTMLElement | null;
  /** ○ */
  onBack?: () => void;
  /** after ✕ activates an element (e.g. move focus on after picking a class) */
  afterActivate?: (el: HTMLElement) => void;
}

/** Inline SVG PS5 face-button glyph, font-independent. */
export function psGlyph(b: 'cross' | 'circle' | 'square' | 'triangle'): string {
  const shape = {
    cross: '<path d="M5 5 L13 13 M13 5 L5 13" />',
    circle: '<circle cx="9" cy="9" r="4.6" />',
    square: '<rect x="4.8" y="4.8" width="8.4" height="8.4" />',
    triangle: '<path d="M9 4.2 L14 13 L4 13 Z" />',
  }[b];
  return `<svg class="psg psg-${b}" viewBox="0 0 18 18" aria-label="${b}"><circle cx="9" cy="9" r="8.2" class="psg-ring"/><g class="psg-mark">${shape}</g></svg>`;
}

/** The pad being played: the connected pad that changed most recently (standard mapping wins a
 *  tie), else any connected pad. A Mac can list a ghost or duplicate pad ahead of the real one (a
 *  stale Bluetooth entry, Steam's virtual pad, a DualSense that re-enumerated after sleep), and
 *  taking the first entry would read a pad nobody is holding. Never throws. */
let activeIx = -1;
export function firstPad(): Gamepad | null {
  let pads: (Gamepad | null)[] = [];
  try { pads = Array.from(navigator.getGamepads?.() ?? []); } catch { return null; }
  let best: Gamepad | null = null;
  for (const gp of pads) {
    if (!gp || !gp.connected) continue;
    if (!best) { best = gp; continue; }
    const t = gp.timestamp ?? 0, bt = best.timestamp ?? 0;
    if (t > bt || (t === bt && gp.mapping === 'standard' && best.mapping !== 'standard')) best = gp;
  }
  // stick with the pad in hand until another one is actually touched (timestamps only move on input)
  const held = activeIx >= 0 ? pads[activeIx] : null;
  if (held && held.connected && best && best !== held && (best.timestamp ?? 0) <= (held.timestamp ?? 0)) return held;
  activeIx = best ? best.index : -1;
  return best;
}

const DIRS = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] } as const;
type Dir = keyof typeof DIRS;

export class PadNav {
  device: Device = 'kbm';
  /** a pad has been seen this session (browsers hide pads until a button is pressed) */
  padSeen = false;
  onDevice: (d: Device) => void = () => {};
  onPadSeen: (seen: boolean) => void = () => {};
  /** Options pressed while a scope is open (the game's own Input handles it in play) */
  onOptions: (() => void) | null = null;
  /** sound hooks: focus moved / a slider stepped, ✕ activated, ○ backed out */
  onMoved: (() => void) | null = null;
  onActivated: (() => void) | null = null;
  onBacked: (() => void) | null = null;
  private scope: NavScope | null = null;
  private focused: HTMLElement | null = null;
  private focusIdx = 0;
  private prev = new Set<number>();
  private holdDir: Dir | null = null;
  private holdNext = 0;
  private holdPolls = 0;
  /** buttons still held from before the scope opened: ignored until released */
  private swallow = new Set<number>();

  constructor() {
    const toKbm = () => this.setDevice('kbm');
    window.addEventListener('keydown', toKbm, true);
    window.addEventListener('mousedown', toKbm, true);
    window.addEventListener('mousemove', (e) => { if (Math.abs(e.movementX) + Math.abs(e.movementY) > 2) toKbm(); }, true);
    window.addEventListener('gamepadconnected', () => this.markSeen(true));
    // deferred so page-level handlers still see device === 'pad' (e.g. auto-pause on unplug)
    window.addEventListener('gamepaddisconnected', () => setTimeout(() => { if (!firstPad()) { this.markSeen(false); this.setDevice('kbm'); } }, 0));
    const tick = () => { this.poll(); requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  }

  setDevice(d: Device) {
    if (d === this.device) return;
    this.device = d;
    document.body.classList.toggle('pad', d === 'pad');
    if (this.focused) this.focused.classList.toggle('padfocus', d === 'pad');
    this.onDevice(d);
  }
  private markSeen(seen: boolean) {
    if (seen === this.padSeen) return;
    this.padSeen = seen;
    document.body.classList.toggle('pad-seen', seen);
    this.onPadSeen(seen);
  }

  /** Open a scope (null closes). Buttons currently held are swallowed until released. */
  open(scope: NavScope | null) {
    this.setFocus(null);
    this.scope = scope;
    this.swallow = new Set(this.prev);
    this.holdDir = null;
    if (scope) this.setFocus(scope.initial?.() ?? this.items()[0] ?? null);
  }
  get current() { return this.focused; }

  focus(el: HTMLElement | null) { this.setFocus(el); }

  private items(): HTMLElement[] {
    if (!this.scope) return [];
    return Array.from(this.scope.root.querySelectorAll<HTMLElement>(this.scope.selector))
      .filter((el) => el.offsetParent !== null && !(el as HTMLButtonElement).disabled);
  }

  private setFocus(el: HTMLElement | null) {
    if (this.focused && this.focused !== el) {
      this.focused.classList.remove('padfocus');
      if (document.activeElement === this.focused) this.focused.blur();
    }
    this.focused = el;
    if (!el) return;
    const list = this.items();
    const i = list.indexOf(el);
    if (i >= 0) this.focusIdx = i;
    if (this.device === 'pad') el.classList.add('padfocus');
    try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch { /* old engines */ }
  }

  /** re-find focus when a menu re-rendered its children */
  private ensureFocus() {
    if (this.focused && this.focused.isConnected && this.focused.offsetParent !== null) return;
    const list = this.items();
    if (!list.length) { this.focused = null; return; }
    this.setFocus(list[Math.min(this.focusIdx, list.length - 1)]);
  }

  private move(dir: Dir) {
    const el = this.focused;
    // sliders take left/right
    if (el instanceof HTMLInputElement && el.type === 'range' && (dir === 'left' || dir === 'right')) {
      const step = Number(el.step) || 1;
      const v = Math.min(Number(el.max), Math.max(Number(el.min), Number(el.value) + (dir === 'right' ? step : -step)));
      const dec = (String(el.step).split('.')[1] ?? '').length;
      el.value = v.toFixed(dec);
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
      this.onMoved?.();
      return;
    }
    const list = this.items();
    if (!list.length) return;
    if (!el) { this.setFocus(list[0]); return; }
    const [dx, dy] = DIRS[dir];
    const a = el.getBoundingClientRect();
    const ax = a.left + a.width / 2, ay = a.top + a.height / 2;
    let best: HTMLElement | null = null, bestScore = Infinity;
    for (const c of list) {
      if (c === el) continue;
      const b = c.getBoundingClientRect();
      // gap along the travel axis (edge to edge), offset across it (centre to centre)
      const along = dx ? (dx > 0 ? b.left - a.right : a.left - b.right) : (dy > 0 ? b.top - a.bottom : a.top - b.bottom);
      const centreAlong = dx ? (b.left + b.width / 2 - ax) * dx : (b.top + b.height / 2 - ay) * dy;
      if (centreAlong <= 1) continue; // not in that direction
      const across = dx ? Math.abs(b.top + b.height / 2 - ay) : Math.abs(b.left + b.width / 2 - ax);
      const overlap = dx ? Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) : Math.min(a.right, b.right) - Math.max(a.left, b.left);
      const score = Math.max(0, along) + across * (overlap > 0 ? 0.3 : 2);
      if (score < bestScore) { bestScore = score; best = c; }
    }
    if (best) { this.setFocus(best); this.onMoved?.(); }
  }

  private activate() {
    const el = this.focused;
    if (!el) return;
    if (el instanceof HTMLInputElement && el.type === 'text') {
      // typing is optional: ✕ puts a keyboard caret in it, ✕ again leaves the default
      if (document.activeElement === el) el.blur(); else { el.focus(); el.select(); }
      return;
    }
    if (el instanceof HTMLInputElement && el.type === 'range') return;
    this.onActivated?.();
    el.click();
    this.scope?.afterActivate?.(el);
  }

  private poll() {
    const gp = firstPad();
    if (!gp) { this.prev.clear(); this.swallow.clear(); return; }
    const now = performance.now();
    const down = new Set<number>();
    gp.buttons.forEach((b, i) => { if (b?.pressed || (b?.value ?? 0) > 0.5) down.add(i); });
    const lx = gp.axes[0] ?? 0, ly = gp.axes[1] ?? 0;
    // the first press is what makes the browser expose the pad at all
    if (!this.padSeen) this.markSeen(true);
    let anyInput = Math.abs(lx) > 0.5 || Math.abs(ly) > 0.5 || Math.abs(gp.axes[2] ?? 0) > 0.5 || Math.abs(gp.axes[3] ?? 0) > 0.5;
    for (const i of down) if (i !== 16 && i !== 17) anyInput = true;
    if (anyInput) this.setDevice('pad');
    for (const i of [...this.swallow]) if (!down.has(i)) this.swallow.delete(i);
    const before = this.prev;
    const pressed = (i: number) => down.has(i) && !before.has(i) && !this.swallow.has(i);
    this.prev = down;
    if (!this.scope) return;
    this.ensureFocus();
    if (this.focused && this.device === 'pad' && !this.focused.classList.contains('padfocus')) this.focused.classList.add('padfocus');

    let dir: Dir | null = null;
    if (down.has(12) || ly < -0.6) dir = 'up';
    else if (down.has(13) || ly > 0.6) dir = 'down';
    else if (down.has(14) || lx < -0.6) dir = 'left';
    else if (down.has(15) || lx > 0.6) dir = 'right';
    // auto-repeat when held; counting polls too means a long frame (asset decode, GC) during a
    // quick tap can't turn it into a double move
    if (dir !== this.holdDir) {
      this.holdDir = dir; this.holdPolls = 0;
      if (dir) { this.move(dir); this.holdNext = now + 380; }
    } else if (dir && ++this.holdPolls > 8 && now >= this.holdNext) {
      this.move(dir); this.holdNext = now + 110;
    }
    if (pressed(0)) this.activate();
    else if (pressed(1)) { if (this.scope?.onBack) this.onBacked?.(); this.scope?.onBack?.(); }
    if (pressed(9)) this.onOptions?.();
  }
}
